import i18n from "i18next";
import { CustomerOrderSummary } from "src/api/generated/erp/sales/customerOrder/service/customerOrderSummary.ts";
import { CustomerOfferSummary } from "src/api/generated/erp/sales/customerOrderOffer/service/customerOfferSummary.ts";
import { keepOnlyObjects } from "src/utils/arrayUtils.ts";
import { formatMoneyWithCurrency, formatNumber } from "src/utils/numberUtils.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";

export interface CustomerOrderOrOfferSummaryField {
	label: string;
	value: string;
}

export type CustomerOrderOrOfferSummaryObjectType = "CUSTOMER_ORDER" | "CUSTOMER_OFFER";

const CUSTOMER_ORDER_CURRENCY_CODE = "EUR";

export function useCustomerOrderOrOfferSummaryFields(
	objectType: CustomerOrderOrOfferSummaryObjectType,
	summary: CustomerOrderSummary | CustomerOfferSummary,
): CustomerOrderOrOfferSummaryField[] {
	const hasPermission = useHasPermission();
	const tenantCustomizations = useTenantCustomizations();

	const isOffer = objectType === "CUSTOMER_OFFER";
	const isOrder = objectType === "CUSTOMER_ORDER";
	const showCostData = isOrder || hasPermission(Permissions.Sales.CustomerOffers.READ_COSTS);

	const offerSummary = isOffer ? (summary as CustomerOfferSummary) : null;
	const orderSummary = isOrder ? (summary as CustomerOrderSummary) : null;

	const tenantExtraFields =
		isOffer ?
			tenantCustomizations.erp?.sales?.customerOfferSummaryExtraFields?.(offerSummary!)
		:	tenantCustomizations.erp?.sales?.customerOrderSummaryExtraFields?.(summary as CustomerOrderSummary);

	const showSumWithoutVat = isOrder || offerSummary?.customerType === "COMPANY";
	const sellingCurrencyCode = offerSummary?.currencyCode ?? orderSummary?.currencyCode ?? CUSTOMER_ORDER_CURRENCY_CODE;
	const domesticCurrencyCode =
		offerSummary?.domesticCurrencyCode ?? orderSummary?.domesticCurrencyCode ?? CUSTOMER_ORDER_CURRENCY_CODE;
	const cost = isOffer ? offerSummary?.totalCostWithCommissions : orderSummary?.totalCost;
	const grossMargin = isOffer ? offerSummary?.grossMarginWithCommissions : orderSummary?.grossMargin;
	const costBeforeCommission = offerSummary?.totalCost;
	const grossMarginBeforeCommission = offerSummary?.grossMargin;

	const fields = [
		showSumWithoutVat && {
			label: i18n.t("sum"),
			value: formatMoneyWithCurrency(summary.totalPrice, sellingCurrencyCode),
		},
		{
			label: i18n.t("sum_with_vat"),
			value: formatMoneyWithCurrency(summary.totalPriceWithVat, sellingCurrencyCode),
		},
		showCostData &&
			cost != null && {
			label: i18n.t("cost"),
			value: formatMoneyWithCurrency(cost, domesticCurrencyCode),
		},
		showCostData &&
			grossMargin != null && {
			label: i18n.t("gross_margin"),
			value: `${formatNumber(grossMargin * 100, { minDigits: 2, maxDigits: 2 })} %`,
		},
		showCostData &&
			isOffer &&
			costBeforeCommission != null && {
				label: i18n.t("cost_before_commission"),
				value: formatMoneyWithCurrency(costBeforeCommission, domesticCurrencyCode),
			},
		showCostData &&
			isOffer &&
			grossMarginBeforeCommission != null && {
				label: i18n.t("gross_margin_before_commission"),
				value: `${formatNumber(grossMarginBeforeCommission * 100, { minDigits: 2, maxDigits: 2 })} %`,
			},
		{
			label: i18n.t("capacity_quantity"),
			value: `${formatNumber(summary.totalCapacityQuantity)}`,
		},
		...(tenantExtraFields ?? []),
	];

	return keepOnlyObjects(fields);
}
